<template>
    <div
        class="groupby-dropdown-wrapper"
        ref="dropdownRef"
    >
        <button
            class="groupby-dropdown-button"
            :aria-expanded="isOpen"
            aria-haspopup="listbox"
            aria-label="Group by selector"
            @click="toggleDropdown"
            @keydown="handleButtonKeyDown"
        >
            <span class="groupby-label">Group by: <span class="dropdown-label">{{ displayLabel }}</span></span>
            <al-icon
                :icon="isOpen ? 'chevron-up' : 'chevron-down'"
                class="groupby-icon"
            />
        </button>
        
        <div
            v-if="isOpen"
            class="groupby-dropdown-menu"
            role="listbox"
        >
            <button
                v-for="option in options"
                :key="option.value"
                role="option"
                :aria-selected="modelValue === option.value"
                tabindex="0"
                class="groupby-dropdown-item"
                :class="{ selected: modelValue === option.value }"
                @click="selectOption(option.value)"
                @keydown="handleItemKeyDown($event, option.value)"
            >
                <span class="item-label">{{ option.label }}</span>
                <img
                    v-if="modelValue === option.value"
                    :src="CheckIcon"
                    alt=""
                    class="check-icon"
                >
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    ref, computed, onMounted, onBeforeUnmount, nextTick
} from 'vue';
import CheckIcon from '@/assets/images/check.svg?url';

interface Option {
    value: string;
    label: string;
}

interface Props {
    modelValue: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    'update:modelValue': [value: string];
}>();

const options: Option[] = [
    { value: 'TEAMS', label: 'Teams' },
    { value: 'NONE', label: 'None' }
];

const isOpen = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);

const displayLabel = computed(() => {
    const option = options.find((opt) => opt.value === props.modelValue);
    return option?.label || 'None';
});

const toggleDropdown = () => {
    isOpen.value = !isOpen.value;
    if (isOpen.value) {
        nextTick(() => {
            const selectedItem = dropdownRef.value?.querySelector('.groupby-dropdown-item.selected') as HTMLElement;
            if (selectedItem) {
                selectedItem.focus();
            } else {
                const firstItem = dropdownRef.value?.querySelector('.groupby-dropdown-item') as HTMLElement;
                firstItem?.focus();
            }
        });
    }
};

const closeDropdown = () => {
    isOpen.value = false;
};

const selectOption = (value: string) => {
    emit('update:modelValue', value);
    closeDropdown();
};

const shouldToggleDropdown = (key: string) => {
    return key === 'Enter' || key === ' ';
};

const shouldCloseDropdown = (key: string) => {
    return key === 'Escape' || (key === 'Tab' && isOpen.value);
};

const handleButtonKeyDown = (event: KeyboardEvent) => {
    const { key } = event;
    
    if (shouldToggleDropdown(key)) {
        event.preventDefault();
        toggleDropdown();
        return;
    }
    
    if (shouldCloseDropdown(key)) {
        closeDropdown();
        return;
    }
    
    if (key === 'ArrowDown' && !isOpen.value) {
        event.preventDefault();
        toggleDropdown();
    }
};

const focusNextItem = (currentItem: HTMLElement) => {
    const nextItem = currentItem.nextElementSibling as HTMLElement;
    if (nextItem) {
        nextItem.focus();
    }
};

const focusPreviousItem = (currentItem: HTMLElement) => {
    const prevItem = currentItem.previousElementSibling as HTMLElement;
    if (prevItem) {
        prevItem.focus();
    }
};

const handleItemKeyDown = (event: KeyboardEvent, value: string) => {
    const { key } = event;
    
    if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        selectOption(value);
        return;
    }
    
    if (key === 'Escape') {
        event.preventDefault();
        closeDropdown();
        const button = dropdownRef.value?.querySelector('.groupby-dropdown-button') as HTMLElement;
        button?.focus();
        return;
    }
    
    if (key === 'Tab') {
        closeDropdown();
        return;
    }
    
    if (key === 'ArrowDown') {
        event.preventDefault();
        focusNextItem(event.target as HTMLElement);
        return;
    }
    
    if (key === 'ArrowUp') {
        event.preventDefault();
        focusPreviousItem(event.target as HTMLElement);
    }
};

const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
        closeDropdown();
    }
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped lang="scss">
.groupby-dropdown-wrapper {
    position: relative;
    display: inline-block;
}

:deep(.groupby-dropdown-button) {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #31708F;
    border: 1px solid #31708F !important;
    border-radius: 4px;
    background-color: #fff;
    height: 36px;
    width: 180px;
    padding: 0 12px 0 16px;
    cursor: pointer;
    font-family: Roboto;
    font-style: normal;
    font-weight: 500;
    font-size: 12.8px;
    transition: all 0.2s ease;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    outline: none !important;
    box-shadow: none !important;
    
    &:hover,
    &[aria-expanded="true"] {
        background-color: #31708F;
        color: #fff;
        border: 1px solid #31708F !important;
        
        .groupby-label,
        .dropdown-label,
        .groupby-icon {
            color: #fff;
        }
    }
    
    &:focus-visible {
        outline: 2px solid #101010 !important;
        outline-offset: 2px;
        border: 1px solid #31708F !important;
    }
}

:deep(.groupby-label) {
    color: #31708F;
    font-weight: 500;
}

:deep(.dropdown-label) {
    font-weight: 400;
    color: #31708F;
}

:deep(.groupby-icon) {
    color: #31708F;
    font-size: 16px;
    transition: color 0.2s ease;
}

:deep(.groupby-dropdown-menu) {
    position: absolute;
    left: 0;
    top: calc(100% + 4px);
    background-color: white;
    border: 1px solid #E0E0E0;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    min-width: 100%;
}

:deep(.groupby-dropdown-item) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 12px 16px;
    text-align: left;
    border: none !important;
    background-color: white;
    font-family: Roboto;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    color: #1A2021;
    cursor: pointer;
    transition: background-color 0.2s ease;
    outline: none !important;
    box-shadow: none !important;
    
    &:hover,
    &:focus {
        background-color: #EEF1F1;
        border: none !important;
    }
    
    &:first-child {
        border-radius: 4px 4px 0 0;
    }
    
    &:last-child {
        border-radius: 0 0 4px 4px;
    }
}

:deep(.item-label) {
    flex: 1;
}

:deep(.check-icon) {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    margin-left: 8px;
}
</style>
